import { useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Head from 'expo-router/head';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import {
  Shield,
  Lock,
  Database,
  Key,
  Check,
  ArrowRight,
  ChevronRight,
} from 'lucide-react-native';
import Animated, {
  FadeInDown,
  FadeInUp,
  FadeIn,
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import Svg, { Circle, Path as SvgPath } from 'react-native-svg';
import {
  useFonts,
  Sora_400Regular,
  Sora_500Medium,
  Sora_600SemiBold,
  Sora_700Bold,
} from '@expo-google-fonts/sora';
import { useAuth, supabase, isSupabaseConfigured } from '@/lib/supabase';
import { loadMyStartupProfileRow } from '@/lib/startup/my-startup-profile';
import { useProfileStore } from '@/lib/state/profile-store';
import { useIsAdmin } from '@/lib/auth/role';
import { deriveDisplayName } from '@/lib/profile/display-name';
import { useScoreStore } from '@/lib/state/score-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useDataroomStore } from '@/lib/state/dataroom-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import {
  getMockDealflow,
  getMockScoreCriteria,
  getMockDataRoomDocuments,
  getMockRecommendations,
  getMockInvestorMatches,
} from '@/lib/state/mock-data-provider';
import { reportError } from '@/lib/errors';
import { PublicNavBar, PublicFooter } from '@/components/PublicLayout';
import {
  startupPricing,
  investorPricing,
  CURRENCY,
  type PricingTier,
} from '@/lib/pricing';
import { webStyle } from '@/lib/platform';

// ─── Constants ──────────────────────────────────────────
const HERO_DARK = ['#0A1A1A', '#0D2626'] as const;
const SECURITY_DARK = '#0F172A';
const TEAL = '#00D9D9';
const TEAL_DIM = 'rgba(0,217,217,0.15)';
const LIGHT_BG = '#F5F5F7';

// ─── Main Component ────────────────────────────────────
export function LaunchLandingPage() {
  const { width } = useWindowDimensions();
  const router = useRouter();
  const isMobile = width < 768;
  const isDesktop = width >= 1024;
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const [fontsLoaded] = useFonts({
    Sora_400Regular,
    Sora_500Medium,
    Sora_600SemiBold,
    Sora_700Bold,
  });

  const sora = (weight: 400 | 500 | 600 | 700) => {
    if (!fontsLoaded) return undefined;
    const map = {
      400: 'Sora_400Regular',
      500: 'Sora_500Medium',
      600: 'Sora_600SemiBold',
      700: 'Sora_700Bold',
    } as const;
    return map[weight];
  };

  // ─── Auth redirect (copied from WebLandingPage) ──────
  const { user, profile, isLoading: authLoading, getLinkedInData } = useAuth();
  // AUTHORITY from the server. `profile.role` is PERSONA and is never 'admin'
  // under the single model (migration 20260807120000), so the admin branch below
  // could never fire from it: an administrator landing here was routed as an
  // investor AND seeded the subscription store with 'investor', which is why the
  // store's admin feature-gate bypass never triggered either.
  const { isAdmin: hasAdminAuthority } = useIsAdmin(user?.id);

  // ONE-SHOT. This effect calls router.replace, and its deps now include an
  // ASYNC value (hasAdminAuthority, from a React Query hook that refetches on
  // window focus). Without this ref the effect re-fires whenever that query
  // settles — yanking the user off whatever screen they had navigated to and
  // back to the dashboard. index.tsx has always guarded its equivalent redirect
  // with hasNavigated + useIsFocused; this landing page never did, and only
  // stopped being safe when I gave it an async dependency.
  const hasRedirected = useRef(false);
  const startupOnboarding = useProfileStore((s) => s.startupOnboarding);
  const investorOnboarding = useProfileStore((s) => s.investorOnboarding);
  const setUserRole = useProfileStore((s) => s.setUserRole);
  const setUser = useProfileStore((s) => s.setUser);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);
  const setDealflow = useDealflowStore((s) => s.setDealflow);
  const setScoreCriteria = useScoreStore((s) => s.setScoreCriteria);
  const setDataRoomDocuments = useDataroomStore((s) => s.setDataRoomDocuments);
  const setAIRecommendations = useScoreStore((s) => s.setAIRecommendations);
  const setInvestorMatches = useScoreStore((s) => s.setInvestorMatches);
  const setSubscriptionUserRole = useSubscriptionStore((s) => s.setUserRole);
  const subscriptionUserRole = useSubscriptionStore((s) => s.userRole);
  const inverioUserRole = useProfileStore((s) => s.userRole);

  useEffect(() => {
    if (inverioUserRole && inverioUserRole !== 'pending' && !subscriptionUserRole) {
      setSubscriptionUserRole(inverioUserRole);
    }
  }, [inverioUserRole, subscriptionUserRole, setSubscriptionUserRole]);

  // JSON-LD structured data for SEO
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'inverio-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Inverio',
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web, iOS, Android',
      description:
        'AI-powered investment readiness scoring platform for startups and investors',
      url: 'https://inverio.ai',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'CHF' },
      provider: {
        '@type': 'Organization',
        name: 'Inverio',
        url: 'https://inverio.ai',
        email: 'support@inverio.ai',
      },
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('inverio-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  // Auth check + redirect
  useEffect(() => {
    if (authLoading) return;
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== '/' &&
      window.location.pathname !== ''
    ) {
      setIsCheckingAuth(false);
      return;
    }

    const loadReturningUserData = async () => {
      if (!user || !profile) {
        setIsCheckingAuth(false);
        return;
      }
      // Already sent them somewhere — never navigate a second time.
      if (hasRedirected.current) {
        setIsCheckingAuth(false);
        return;
      }
      const userRole = (hasAdminAuthority ? 'admin' : profile.role) as 'startup' | 'investor' | 'admin';
      if (userRole === 'admin') {
        setUserRole('admin');
        setSubscriptionUserRole('admin');
        setUser({
          id: user.id,
          name: deriveDisplayName(profile, user),
          email: profile.email || user.email || '',
        });
        hasRedirected.current = true;
        router.replace('/(admin)/admin-dashboard');
        return;
      }
      const onboarding =
        userRole === 'startup' ? startupOnboarding : investorOnboarding;
      const hasCompletedOnboarding =
        onboarding?.completedAt != null || profile.onboarding_completed;
      if (!hasCompletedOnboarding) {
        hasRedirected.current = true;
        router.replace(`/login?role=${userRole}`);
        return;
      }
      setUserRole(userRole);
      setSubscriptionUserRole(userRole);
      const linkedInData = getLinkedInData();
      setUser({
        id: user.id,
        // profiles.first_name/last_name wins over the raw LinkedIn/metadata name.
        name: deriveDisplayName(profile, user),
        email: profile.email || user.email || '',
        avatarUrl:
          linkedInData.avatarUrl ||
          user.user_metadata?.avatar_url ||
          user.user_metadata?.picture ||
          undefined,
        linkedIn: linkedInData.linkedInUrl || undefined,
      });
      if (isSupabaseConfigured()) {
        try {
          if (userRole === 'startup') {
            // Resolve via the membership seam — `.eq('user_id', user.id)` is
            // NULL for create-path founders and hydrated nothing for them.
            const dbProfile = (await loadMyStartupProfileRow(supabase)) as any;
            if (dbProfile) {
              setStartupProfile({
                id: dbProfile.id,
                name: dbProfile.name,
                tagline: dbProfile.tagline ?? '',
                industry: dbProfile.industry,
                stage: dbProfile.stage,
                fundingTarget: dbProfile.funding_target ?? 0,
                location: dbProfile.location ?? '',
                foundedYear:
                  dbProfile.founded_year ?? new Date().getFullYear(),
                teamSize: dbProfile.team_size ?? 1,
                website: dbProfile.website ?? undefined,
                linkedIn: dbProfile.linkedin ?? undefined,
                problem: dbProfile.problem ?? undefined,
                solution: dbProfile.solution ?? undefined,
                targetCustomer: dbProfile.target_customer ?? undefined,
                monthlyRevenue: dbProfile.monthly_revenue ?? undefined,
                userCount: dbProfile.user_count ?? undefined,
                growthRate: dbProfile.growth_rate ?? undefined,
                marketSize: dbProfile.market_size ?? undefined,
                competitors: dbProfile.competitors ?? undefined,
                isActivelyRaising: dbProfile.is_actively_raising ?? false,
                dataRoomConnected: dbProfile.data_room_connected ?? false,
                dataRoomProvider: dbProfile.data_room_provider ?? undefined,
                isSerialFounder: dbProfile.is_serial_founder ?? false,
                previousVentures: dbProfile.previous_ventures ?? undefined,
              });
            }
            setScoreCriteria(getMockScoreCriteria());
            setDataRoomDocuments(getMockDataRoomDocuments());
            setAIRecommendations(getMockRecommendations());
            setInvestorMatches(getMockInvestorMatches());
          } else {
            const { data: dbProfile } = (await supabase
              .from('investor_profiles')
              .select('*')
              .eq('user_id', user.id)
              .single()) as { data: any };
            if (dbProfile) {
              setInvestorProfile({
                id: dbProfile.id,
                firm: dbProfile.firm ?? undefined,
                investmentFocus: dbProfile.investment_focus ?? [],
                stagePreference: dbProfile.stage_preference ?? [],
                ticketSize: {
                  min: dbProfile.ticket_size_min ?? 0,
                  max: dbProfile.ticket_size_max ?? 0,
                },
                location: dbProfile.location ?? '',
                portfolioCount: dbProfile.portfolio_count ?? 0,
                linkedInVerified: dbProfile.linkedin_verified ?? false,
                linkedInUrl: dbProfile.linkedin_url ?? undefined,
                linkedInVerifiedAt:
                  dbProfile.linkedin_verified_at ?? undefined,
              });
            }
            setDealflow(getMockDealflow());
                }
        } catch (error) {
          reportError(error, {
            context: 'LaunchLandingPage.loadReturningUserData',
            level: 'warning',
            silent: true,
          });
        }
      }
      hasRedirected.current = true;
      router.replace(
        userRole === 'startup'
          ? '/(startup)/startup-dashboard'
          : '/(investor)/investor-dashboard',
      );
    };
    loadReturningUserData();
  }, [user, profile, authLoading, startupOnboarding, investorOnboarding, router, hasAdminAuthority]);

  if (isCheckingAuth || authLoading) {
    return (
      <View
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: '#0A1A1A' }}
      >
        <ActivityIndicator size="large" color={TEAL} />
      </View>
    );
  }

  return (
    <ScrollView
      className="flex-1"
      style={{ backgroundColor: '#FFFFFF' }}
      showsVerticalScrollIndicator={false}
    >
      {Platform.OS === 'web' && (
        <Head>
          <title>Inverio — AI-Powered Investment Readiness Scoring</title>
          <meta name="description" content="Help startups become investor-ready with AI scoring, document analysis, and investor matching. Built for the Swiss market." />
          <link rel="canonical" href="https://inverio.ai/" />
          <meta property="og:title" content="Inverio — AI-Powered Investment Readiness Scoring" />
          <meta property="og:description" content="Help startups become investor-ready with AI scoring, document analysis, and investor matching. Built for the Swiss market." />
          <meta property="og:url" content="https://inverio.ai/" />
        </Head>
      )}
      <PublicNavBar />
      <HeroSection
        isMobile={isMobile}
        isDesktop={isDesktop}
        sora={sora}
        router={router}
      />
      <TrustBar isMobile={isMobile} sora={sora} />
      <StartupSection
        isMobile={isMobile}
        isDesktop={isDesktop}
        sora={sora}
        router={router}
      />
      <InvestorSection
        isMobile={isMobile}
        isDesktop={isDesktop}
        sora={sora}
        router={router}
      />
      <HowItWorks isMobile={isMobile} isDesktop={isDesktop} sora={sora} />
      <PricingPreview
        isMobile={isMobile}
        isDesktop={isDesktop}
        sora={sora}
        router={router}
      />
      <SecuritySection isMobile={isMobile} isDesktop={isDesktop} sora={sora} />
      <FinalCTA isMobile={isMobile} sora={sora} router={router} />
      <PublicFooter />
    </ScrollView>
  );
}

// ─── Types ──────────────────────────────────────────────
type SoraFn = (weight: 400 | 500 | 600 | 700) => string | undefined;
type RouterType = ReturnType<typeof useRouter>;

// ─── 1. Dark Hero ───────────────────────────────────────
function HeroSection({
  isMobile,
  isDesktop,
  sora,
  router,
}: {
  isMobile: boolean;
  isDesktop: boolean;
  sora: SoraFn;
  router: RouterType;
}) {
  return (
    <LinearGradient
      colors={[...HERO_DARK]}
      start={{ x: 0.3, y: 0 }}
      end={{ x: 0.7, y: 1 }}
      style={{
        paddingHorizontal: isMobile ? 20 : 64,
        paddingTop: isMobile ? 48 : 80,
        paddingBottom: isMobile ? 32 : 40,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle glow orb */}
      <View
        style={{
          position: 'absolute',
          top: isMobile ? -60 : -100,
          right: isMobile ? -40 : '15%',
          width: isMobile ? 280 : 500,
          height: isMobile ? 280 : 500,
          borderRadius: 999,
          backgroundColor: 'rgba(0,217,217,0.06)',
        }}
      />

      {/* Main content: split on desktop, stacked on mobile */}
      <View
        style={{
          maxWidth: 1200,
          alignSelf: 'center',
          width: '100%',
          flexDirection: isDesktop ? 'row' : 'column',
          alignItems: isDesktop ? 'center' : 'center',
          gap: isDesktop ? 48 : 36,
        }}
      >
        {/* Left: text + CTAs */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(700)}
          style={{
            flex: isDesktop ? 0.48 : undefined,
            alignItems: isDesktop ? 'flex-start' : 'center',
            width: '100%',
          }}
        >
          <Text
            style={{
              fontFamily: sora(700),
              fontSize: isMobile ? 32 : isDesktop ? 50 : 44,
              lineHeight: isMobile ? 40 : isDesktop ? 60 : 52,
              color: '#FFFFFF',
              textAlign: isDesktop ? 'left' : 'center',
              marginBottom: 18,
            }}
          >
            Know Your Score.{'\n'}Find Your Investors.
          </Text>

          <Text
            style={{
              fontFamily: sora(400),
              fontSize: isMobile ? 16 : 19,
              lineHeight: isMobile ? 24 : 29,
              color: 'rgba(255,255,255,0.7)',
              textAlign: isDesktop ? 'left' : 'center',
              maxWidth: 480,
              marginBottom: 32,
            }}
          >
            The AI-powered platform that scores your investment readiness,
            manages your data room, and connects you with the right investors.
          </Text>

          {/* Dual CTAs */}
          <View
            style={{
              flexDirection: isMobile ? 'column' : 'row',
              gap: 14,
              width: isMobile ? '100%' : undefined,
              marginBottom: 24,
            }}
          >
            <AnimatedPressable
              onPress={() => router.push('/login?role=startup')}
              style={{
                backgroundColor: TEAL,
                paddingHorizontal: 28,
                paddingVertical: 15,
                borderRadius: 12,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Text
                style={{
                  fontFamily: sora(600),
                  fontSize: 15,
                  color: '#0A1A1A',
                }}
              >
                Get Your Score
              </Text>
              <ArrowRight size={17} color="#0A1A1A" />
            </AnimatedPressable>

            <AnimatedPressable
              onPress={() => router.push('/login?role=investor')}
              style={{
                paddingHorizontal: 28,
                paddingVertical: 15,
                borderRadius: 12,
                borderWidth: 1.5,
                borderColor: 'rgba(255,255,255,0.3)',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Text
                style={{
                  fontFamily: sora(600),
                  fontSize: 15,
                  color: '#FFFFFF',
                }}
              >
                Explore Dealflow
              </Text>
              <ArrowRight size={17} color="#FFFFFF" />
            </AnimatedPressable>
          </View>

          {/* App Store Badges */}
          <View
            style={{
              flexDirection: 'row',
              gap: 12,
              marginBottom: isDesktop ? 0 : 8,
              justifyContent: isDesktop ? 'flex-start' : 'center',
            }}
          >
            <AppStoreBadge sora={sora} />
            <GooglePlayBadge sora={sora} />
          </View>
        </Animated.View>

        {/* Right: Device mockups */}
        <Animated.View
          entering={FadeInUp.delay(300).duration(800)}
          style={{
            flex: isDesktop ? 0.52 : undefined,
            width: '100%',
            maxWidth: isMobile ? 400 : undefined,
            alignSelf: 'center',
          }}
        >
          <DeviceMockups sora={sora} isMobile={isMobile} isDesktop={isDesktop} />
        </Animated.View>
      </View>

    </LinearGradient>
  );
}

// ─── App Store Badge ────────────────────────────────────
function AppStoreBadge({ sora }: { sora: SoraFn }) {
  return (
    <AnimatedPressable
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: '#000000',
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
      }}
    >
      {/* Apple icon */}
      <Svg width={20} height={24} viewBox="0 0 20 24">
        <SvgPath
          d="M15.77 12.77c-.03-2.96 2.42-4.39 2.53-4.46-1.38-2.01-3.52-2.29-4.28-2.32-1.82-.19-3.56 1.07-4.49 1.07-.93 0-2.36-1.05-3.88-1.02-2 .03-3.84 1.16-4.87 2.95-2.08 3.61-.53 8.96 1.49 11.9.99 1.44 2.18 3.06 3.73 3 1.5-.06 2.06-.97 3.87-.97 1.8 0 2.32.97 3.9.94 1.61-.03 2.63-1.46 3.61-2.91 1.14-1.67 1.61-3.28 1.64-3.37-.04-.01-3.14-1.2-3.17-4.78l-.08-.03zM12.84 4.05c.82-1 1.38-2.38 1.23-3.77-1.19.05-2.63.79-3.48 1.79-.77.88-1.44 2.3-1.26 3.66 1.33.1 2.68-.68 3.51-1.68z"
          fill="#FFFFFF"
        />
      </Svg>
      <View>
        <Text
          style={{
            fontFamily: sora(400),
            fontSize: 8,
            color: 'rgba(255,255,255,0.8)',
            lineHeight: 10,
          }}
        >
          Download on the
        </Text>
        <Text
          style={{
            fontFamily: sora(600),
            fontSize: 14,
            color: '#FFFFFF',
            lineHeight: 18,
          }}
        >
          App Store
        </Text>
      </View>
    </AnimatedPressable>
  );
}

// ─── Google Play Badge ──────────────────────────────────
function GooglePlayBadge({ sora }: { sora: SoraFn }) {
  return (
    <AnimatedPressable
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: '#000000',
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
      }}
    >
      {/* Play triangle icon */}
      <Svg width={20} height={22} viewBox="0 0 20 22">
        <SvgPath d="M1 1.5l10.5 9.5L1 20.5V1.5z" fill="#34C759" />
        <SvgPath d="M1 1.5l13 7.5-2.5 2.5L1 1.5z" fill="#FF3B30" />
        <SvgPath d="M14 9l4.5 2-4.5 2-2.5-2L14 9z" fill="#FF9500" />
        <SvgPath d="M1 20.5l10.5-9.5 2.5 2.5L1 20.5z" fill="#00B8B8" />
      </Svg>
      <View>
        <Text
          style={{
            fontFamily: sora(400),
            fontSize: 8,
            color: 'rgba(255,255,255,0.8)',
            lineHeight: 10,
          }}
        >
          GET IT ON
        </Text>
        <Text
          style={{
            fontFamily: sora(600),
            fontSize: 14,
            color: '#FFFFFF',
            lineHeight: 18,
          }}
        >
          Google Play
        </Text>
      </View>
    </AnimatedPressable>
  );
}

// ─── Device Mockups (Phone + Laptop) ────────────────────
function DeviceMockups({
  sora,
  isMobile,
  isDesktop,
}: {
  sora: SoraFn;
  isMobile: boolean;
  isDesktop: boolean;
}) {
  const laptopW = isMobile ? 300 : isDesktop ? 420 : 380;
  const laptopH = isMobile ? 190 : isDesktop ? 270 : 240;
  const phoneW = isMobile ? 110 : isDesktop ? 155 : 140;
  const phoneH = isMobile ? 200 : isDesktop ? 290 : 260;

  return (
    <View
      style={{
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        paddingBottom: isMobile ? 40 : 60,
      }}
    >
      {/* Laptop */}
      <View style={{ alignItems: 'center' }}>
        {/* Screen */}
        <View
          style={{
            width: laptopW,
            height: laptopH,
            borderRadius: 10,
            backgroundColor: '#1C1C1E',
            borderWidth: 2,
            borderColor: '#3A3A3C',
            overflow: 'hidden',
          }}
        >
          {/* Browser chrome */}
          <View
            style={{
              height: 24,
              backgroundColor: '#2C2C2E',
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: 10,
              gap: 5,
            }}
          >
            <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: '#FF5F57' }} />
            <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: '#FFBD2E' }} />
            <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: '#28C840' }} />
            <View
              style={{
                flex: 1,
                height: 14,
                borderRadius: 4,
                backgroundColor: '#3A3A3C',
                marginLeft: 10,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text style={{ fontSize: 7, color: '#8E8E93', fontFamily: sora(400) }}>
                inverio.ai
              </Text>
            </View>
          </View>

          {/* Web dashboard content */}
          <View style={{ flex: 1, padding: isMobile ? 8 : 14, backgroundColor: '#F5F5F7' }}>
            {/* Top nav mock */}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: isMobile ? 6 : 10,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <View
                  style={{
                    width: isMobile ? 14 : 18,
                    height: isMobile ? 14 : 18,
                    borderRadius: 4,
                    backgroundColor: TEAL,
                  }}
                />
                <Text style={{ fontSize: isMobile ? 7 : 9, color: '#1D1D1F', fontFamily: sora(600) }}>
                  Inverio Dashboard
                </Text>
              </View>
              <View
                style={{
                  width: isMobile ? 16 : 20,
                  height: isMobile ? 16 : 20,
                  borderRadius: 10,
                  backgroundColor: '#D2D2D7',
                }}
              />
            </View>

            {/* Dashboard cards row */}
            <View style={{ flexDirection: 'row', gap: isMobile ? 4 : 8 }}>
              {/* Score card */}
              <View
                style={{
                  flex: 1,
                  backgroundColor: '#FFFFFF',
                  borderRadius: 6,
                  padding: isMobile ? 6 : 10,
                  alignItems: 'center',
                }}
              >
                <Text style={{ fontSize: isMobile ? 6 : 7, color: '#8E8E93', fontFamily: sora(400) }}>
                  Score
                </Text>
                <Text
                  style={{
                    fontSize: isMobile ? 16 : 22,
                    color: TEAL,
                    fontFamily: sora(700),
                    marginVertical: 2,
                  }}
                >
                  78
                </Text>
                <View
                  style={{
                    width: '80%',
                    height: 3,
                    borderRadius: 2,
                    backgroundColor: '#E8E8ED',
                    overflow: 'hidden',
                  }}
                >
                  <View
                    style={{
                      width: '78%',
                      height: '100%',
                      backgroundColor: TEAL,
                      borderRadius: 2,
                    }}
                  />
                </View>
              </View>
              {/* Investors card */}
              <View
                style={{
                  flex: 1,
                  backgroundColor: '#FFFFFF',
                  borderRadius: 6,
                  padding: isMobile ? 6 : 10,
                  alignItems: 'center',
                }}
              >
                <Text style={{ fontSize: isMobile ? 6 : 7, color: '#8E8E93', fontFamily: sora(400) }}>
                  Matches
                </Text>
                <Text
                  style={{
                    fontSize: isMobile ? 16 : 22,
                    color: '#34C759',
                    fontFamily: sora(700),
                    marginVertical: 2,
                  }}
                >
                  12
                </Text>
                <Text style={{ fontSize: isMobile ? 5 : 6, color: '#34C759', fontFamily: sora(500) }}>
                  +3 new
                </Text>
              </View>
              {/* Data Room card */}
              <View
                style={{
                  flex: 1,
                  backgroundColor: '#FFFFFF',
                  borderRadius: 6,
                  padding: isMobile ? 6 : 10,
                  alignItems: 'center',
                }}
              >
                <Text style={{ fontSize: isMobile ? 6 : 7, color: '#8E8E93', fontFamily: sora(400) }}>
                  Documents
                </Text>
                <Text
                  style={{
                    fontSize: isMobile ? 16 : 22,
                    color: '#FF9500',
                    fontFamily: sora(700),
                    marginVertical: 2,
                  }}
                >
                  8
                </Text>
                <Text style={{ fontSize: isMobile ? 5 : 6, color: '#8E8E93', fontFamily: sora(500) }}>
                  uploaded
                </Text>
              </View>
            </View>

            {/* Criteria mini bars */}
            <View
              style={{
                marginTop: isMobile ? 6 : 10,
                backgroundColor: '#FFFFFF',
                borderRadius: 6,
                padding: isMobile ? 6 : 10,
              }}
            >
              {[
                { label: 'Team', pct: 85, color: '#34C759' },
                { label: 'Market', pct: 72, color: TEAL },
                { label: 'Traction', pct: 65, color: '#FF9500' },
              ].map((c) => (
                <View key={c.label} style={{ marginBottom: 4 }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: 2,
                    }}
                  >
                    <Text style={{ fontSize: isMobile ? 5 : 7, color: '#636366', fontFamily: sora(400) }}>
                      {c.label}
                    </Text>
                    <Text style={{ fontSize: isMobile ? 5 : 7, color: '#1D1D1F', fontFamily: sora(600) }}>
                      {c.pct}%
                    </Text>
                  </View>
                  <View
                    style={{
                      height: 3,
                      borderRadius: 2,
                      backgroundColor: '#F5F5F7',
                      overflow: 'hidden',
                    }}
                  >
                    <View
                      style={{
                        width: `${c.pct}%`,
                        height: '100%',
                        backgroundColor: c.color,
                        borderRadius: 2,
                      }}
                    />
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>
        {/* Laptop base/hinge */}
        <View
          style={{
            width: laptopW + 30,
            height: 10,
            borderBottomLeftRadius: 6,
            borderBottomRightRadius: 6,
            backgroundColor: '#3A3A3C',
            marginTop: -1,
          }}
        />
        <View
          style={{
            width: laptopW + 60,
            height: 4,
            borderRadius: 2,
            backgroundColor: '#48484A',
          }}
        />
      </View>

      {/* Phone — overlapping bottom-right */}
      <View
        style={{
          position: 'absolute',
          bottom: isMobile ? 0 : 10,
          right: isMobile ? '2%' : isDesktop ? '2%' : '5%',
        }}
      >
        <View
          style={{
            width: phoneW,
            height: phoneH,
            borderRadius: isMobile ? 16 : 22,
            backgroundColor: '#1C1C1E',
            borderWidth: 2,
            borderColor: '#3A3A3C',
            overflow: 'hidden',
            shadowColor: '#000',
            shadowOffset: { width: -4, height: 8 },
            shadowOpacity: 0.4,
            shadowRadius: 20,
            elevation: 10,
          }}
        >
          {/* Notch */}
          <View
            style={{
              alignSelf: 'center',
              width: phoneW * 0.35,
              height: isMobile ? 10 : 14,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
              backgroundColor: '#1C1C1E',
              zIndex: 10,
            }}
          />
          {/* Phone screen content */}
          <View
            style={{
              flex: 1,
              backgroundColor: '#F5F5F7',
              margin: 2,
              borderRadius: isMobile ? 14 : 20,
              padding: isMobile ? 8 : 12,
              overflow: 'hidden',
            }}
          >
            {/* Status bar */}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: isMobile ? 6 : 10,
              }}
            >
              <Text style={{ fontSize: isMobile ? 6 : 8, color: '#1D1D1F', fontFamily: sora(600) }}>
                9:41
              </Text>
              <View style={{ flexDirection: 'row', gap: 3 }}>
                <View style={{ width: 10, height: 5, borderRadius: 1, backgroundColor: '#1D1D1F' }} />
                <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: '#1D1D1F' }} />
              </View>
            </View>

            {/* Score display */}
            <View style={{ alignItems: 'center', marginBottom: isMobile ? 6 : 10 }}>
              <Text style={{ fontSize: isMobile ? 5 : 7, color: '#8E8E93', fontFamily: sora(500) }}>
                Your Score
              </Text>
              <Text
                style={{
                  fontSize: isMobile ? 24 : 34,
                  color: TEAL,
                  fontFamily: sora(700),
                }}
              >
                78
              </Text>
              <View
                style={{
                  paddingHorizontal: 8,
                  paddingVertical: 2,
                  borderRadius: 8,
                  backgroundColor: 'rgba(52,199,89,0.12)',
                  marginTop: 2,
                }}
              >
                <Text style={{ fontSize: isMobile ? 5 : 7, color: '#34C759', fontFamily: sora(500) }}>
                  +5 pts
                </Text>
              </View>
            </View>

            {/* Mini criteria */}
            {[
              { label: 'Team', pct: 85, color: '#34C759' },
              { label: 'Market', pct: 72, color: TEAL },
              { label: 'Traction', pct: 65, color: '#FF9500' },
            ].map((c) => (
              <View key={c.label} style={{ marginBottom: isMobile ? 4 : 6 }}>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: 2,
                  }}
                >
                  <Text style={{ fontSize: isMobile ? 5 : 7, color: '#636366', fontFamily: sora(400) }}>
                    {c.label}
                  </Text>
                  <Text style={{ fontSize: isMobile ? 5 : 7, color: '#1D1D1F', fontFamily: sora(600) }}>
                    {c.pct}%
                  </Text>
                </View>
                <View
                  style={{
                    height: isMobile ? 3 : 4,
                    borderRadius: 2,
                    backgroundColor: '#E8E8ED',
                    overflow: 'hidden',
                  }}
                >
                  <View
                    style={{
                      width: `${c.pct}%`,
                      height: '100%',
                      backgroundColor: c.color,
                      borderRadius: 2,
                    }}
                  />
                </View>
              </View>
            ))}

            {/* CTA button mock */}
            <View
              style={{
                backgroundColor: TEAL,
                borderRadius: 6,
                paddingVertical: isMobile ? 4 : 6,
                alignItems: 'center',
                marginTop: isMobile ? 4 : 8,
              }}
            >
              <Text style={{ fontSize: isMobile ? 5 : 7, color: '#0A1A1A', fontFamily: sora(600) }}>
                View Details
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─── 2. Trust / Logo Bar ────────────────────────────────
function TrustBar({
  isMobile,
  sora,
}: {
  isMobile: boolean;
  sora: SoraFn;
}) {
  const integrations = ['LinkedIn', 'Google Drive', 'Stripe', 'Supabase'];

  return (
    <Animated.View
      entering={FadeIn.delay(300).duration(600)}
      style={{
        backgroundColor: LIGHT_BG,
        paddingVertical: isMobile ? 24 : 32,
        paddingHorizontal: isMobile ? 20 : 64,
      }}
    >
      <View
        style={{
          maxWidth: 1000,
          alignSelf: 'center',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Text
          style={{
            fontFamily: sora(500),
            fontSize: 12,
            color: '#8E8E93',
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            marginBottom: 16,
          }}
        >
          Integrated With
        </Text>

        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 12,
            marginBottom: 16,
          }}
        >
          {integrations.map((name) => (
            <View
              key={name}
              style={{
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 8,
                backgroundColor: '#FFFFFF',
                borderWidth: 1,
                borderColor: '#E8E8ED',
              }}
            >
              <Text
                style={{
                  fontFamily: sora(500),
                  fontSize: 13,
                  color: '#636366',
                }}
              >
                {name}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            paddingHorizontal: 14,
            paddingVertical: 6,
            borderRadius: 20,
            backgroundColor: 'rgba(0,217,217,0.08)',
          }}
        >
          <Shield size={14} color={TEAL} />
          <Text
            style={{
              fontFamily: sora(500),
              fontSize: 12,
              color: '#009999',
            }}
          >
            Data hosted in Frankfurt, Germany (EU)
          </Text>
        </View>
      </View>
    </Animated.View>
  );
}

// ─── 3. For Startups — Asymmetric Split ─────────────────
function StartupSection({
  isMobile,
  isDesktop,
  sora,
  router,
}: {
  isMobile: boolean;
  isDesktop: boolean;
  sora: SoraFn;
  router: RouterType;
}) {
  const bullets = [
    'AI-generated investment readiness score across 15+ criteria',
    'Secure data room with investor access controls',
    'Personalized improvement roadmap and action items',
    'Direct matching with verified investors',
  ];

  return (
    <Animated.View
      entering={FadeInUp.delay(200).duration(600)}
      style={{
        paddingHorizontal: isMobile ? 20 : 64,
        paddingVertical: isMobile ? 48 : 80,
        backgroundColor: '#FFFFFF',
      }}
    >
      <View
        style={{
          maxWidth: 1100,
          alignSelf: 'center',
          width: '100%',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 36 : 60,
          alignItems: isMobile ? 'stretch' : 'center',
        }}
      >
        {/* Text side */}
        <View style={{ flex: isMobile ? undefined : 0.45 }}>
          <Text
            style={{
              fontFamily: sora(600),
              fontSize: 13,
              color: TEAL,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            For Startups
          </Text>
          <Text
            style={{
              fontFamily: sora(700),
              fontSize: isMobile ? 26 : 36,
              lineHeight: isMobile ? 34 : 44,
              color: '#1D1D1F',
              marginBottom: 20,
            }}
          >
            Get Investment Ready{'\n'}with AI
          </Text>
          {bullets.map((b, i) => (
            <View
              key={i}
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 10,
                marginBottom: 14,
              }}
            >
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  backgroundColor: TEAL_DIM,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 1,
                }}
              >
                <Check size={13} color={TEAL} strokeWidth={3} />
              </View>
              <Text
                style={{
                  fontFamily: sora(400),
                  fontSize: 15,
                  color: '#636366',
                  lineHeight: 22,
                  flex: 1,
                }}
              >
                {b}
              </Text>
            </View>
          ))}
          <AnimatedPressable
            onPress={() => router.push('/login?role=startup')}
            style={{
              backgroundColor: TEAL,
              paddingHorizontal: 28,
              paddingVertical: 14,
              borderRadius: 10,
              alignSelf: 'flex-start',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
            }}
          >
            <Text
              style={{
                fontFamily: sora(600),
                fontSize: 15,
                color: '#0A1A1A',
              }}
            >
              Start Free Assessment
            </Text>
            <ChevronRight size={16} color="#0A1A1A" />
          </AnimatedPressable>
        </View>

        {/* Mockup side */}
        <View style={{ flex: isMobile ? undefined : 0.55 }}>
          <StartupDashboardMockup sora={sora} isMobile={isMobile} />
        </View>
      </View>
    </Animated.View>
  );
}

// ─── Startup Dashboard Mockup ───────────────────────────
function StartupDashboardMockup({
  sora,
  isMobile,
}: {
  sora: SoraFn;
  isMobile: boolean;
}) {
  const criteria = [
    { label: 'Team', pct: 85, color: '#34C759' },
    { label: 'Market', pct: 72, color: TEAL },
    { label: 'Traction', pct: 65, color: '#FF9500' },
    { label: 'Financials', pct: 58, color: '#FF9500' },
  ];

  const ringSize = isMobile ? 100 : 120;
  const strokeWidth = 8;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = 0.78;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <View
      style={webStyle(
        {
          backgroundColor: '#FFFFFF',
          borderRadius: 20,
          padding: isMobile ? 20 : 28,
          borderWidth: 1,
          borderColor: '#E8E8ED',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.08,
          shadowRadius: 24,
          elevation: 6,
        },
        { transform: [{ rotate: '-1deg' }] },
      )}
    >
      {/* Header */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <Text
          style={{
            fontFamily: sora(600),
            fontSize: 16,
            color: '#1D1D1F',
          }}
        >
          Investment Readiness
        </Text>
        <View
          style={{
            paddingHorizontal: 10,
            paddingVertical: 4,
            borderRadius: 12,
            backgroundColor: 'rgba(52,199,89,0.12)',
          }}
        >
          <Text
            style={{
              fontFamily: sora(500),
              fontSize: 11,
              color: '#34C759',
            }}
          >
            +5 this week
          </Text>
        </View>
      </View>

      {/* Score ring + criteria */}
      <View
        style={{
          flexDirection: 'row',
          gap: isMobile ? 16 : 24,
          alignItems: 'center',
        }}
      >
        {/* Ring */}
        <View
          style={{
            width: ringSize,
            height: ringSize,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Svg width={ringSize} height={ringSize}>
            <Circle
              cx={ringSize / 2}
              cy={ringSize / 2}
              r={radius}
              stroke="#E8E8ED"
              strokeWidth={strokeWidth}
              fill="none"
            />
            <Circle
              cx={ringSize / 2}
              cy={ringSize / 2}
              r={radius}
              stroke={TEAL}
              strokeWidth={strokeWidth}
              fill="none"
              strokeDasharray={`${circumference}`}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              rotation="-90"
              origin={`${ringSize / 2}, ${ringSize / 2}`}
            />
          </Svg>
          <View style={{ position: 'absolute', alignItems: 'center' }}>
            <Text
              style={{
                fontFamily: sora(700),
                fontSize: isMobile ? 28 : 34,
                color: '#1D1D1F',
              }}
            >
              78
            </Text>
            <Text
              style={{
                fontFamily: sora(400),
                fontSize: 10,
                color: '#8E8E93',
              }}
            >
              / 100
            </Text>
          </View>
        </View>

        {/* Criteria bars */}
        <View style={{ flex: 1, gap: 10 }}>
          {criteria.map((c) => (
            <View key={c.label}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <Text
                  style={{
                    fontFamily: sora(500),
                    fontSize: 12,
                    color: '#636366',
                  }}
                >
                  {c.label}
                </Text>
                <Text
                  style={{
                    fontFamily: sora(600),
                    fontSize: 12,
                    color: '#1D1D1F',
                  }}
                >
                  {c.pct}%
                </Text>
              </View>
              <View
                style={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#F5F5F7',
                  overflow: 'hidden',
                }}
              >
                <View
                  style={{
                    width: `${c.pct}%`,
                    height: '100%',
                    borderRadius: 3,
                    backgroundColor: c.color,
                  }}
                />
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* AI recommendation */}
      <View
        style={{
          marginTop: 18,
          padding: 14,
          borderRadius: 12,
          backgroundColor: LIGHT_BG,
          borderLeftWidth: 3,
          borderLeftColor: TEAL,
        }}
      >
        <Text
          style={{
            fontFamily: sora(400),
            fontSize: 13,
            color: '#636366',
            lineHeight: 19,
            fontStyle: 'italic',
          }}
        >
          "Strong team composition. Focus on improving monthly revenue metrics
          to unlock higher investor tiers."
        </Text>
      </View>
    </View>
  );
}

// ─── 4. For Investors — Reversed Asymmetric ─────────────
function InvestorSection({
  isMobile,
  isDesktop,
  sora,
  router,
}: {
  isMobile: boolean;
  isDesktop: boolean;
  sora: SoraFn;
  router: RouterType;
}) {
  const bullets = [
    'Browse pre-vetted startups with objective AI scores',
    'Filter by industry, stage, ticket size, and geography',
    'Access secure data rooms and due diligence docs',
    'Track portfolio performance and co-invest with peers',
  ];

  return (
    <Animated.View
      entering={FadeInUp.delay(200).duration(600)}
      style={{
        paddingHorizontal: isMobile ? 20 : 64,
        paddingVertical: isMobile ? 48 : 80,
        backgroundColor: LIGHT_BG,
      }}
    >
      <View
        style={{
          maxWidth: 1100,
          alignSelf: 'center',
          width: '100%',
          flexDirection: isMobile ? 'column' : 'row-reverse',
          gap: isMobile ? 36 : 60,
          alignItems: isMobile ? 'stretch' : 'center',
        }}
      >
        {/* Text side */}
        <View style={{ flex: isMobile ? undefined : 0.45 }}>
          <Text
            style={{
              fontFamily: sora(600),
              fontSize: 13,
              color: TEAL,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            For Investors
          </Text>
          <Text
            style={{
              fontFamily: sora(700),
              fontSize: isMobile ? 26 : 36,
              lineHeight: isMobile ? 34 : 44,
              color: '#1D1D1F',
              marginBottom: 20,
            }}
          >
            Discover Pre-Vetted{'\n'}Dealflow
          </Text>
          {bullets.map((b, i) => (
            <View
              key={i}
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 10,
                marginBottom: 14,
              }}
            >
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  backgroundColor: TEAL_DIM,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 1,
                }}
              >
                <Check size={13} color={TEAL} strokeWidth={3} />
              </View>
              <Text
                style={{
                  fontFamily: sora(400),
                  fontSize: 15,
                  color: '#636366',
                  lineHeight: 22,
                  flex: 1,
                }}
              >
                {b}
              </Text>
            </View>
          ))}
          <AnimatedPressable
            onPress={() => router.push('/login?role=investor')}
            style={{
              backgroundColor: TEAL,
              paddingHorizontal: 28,
              paddingVertical: 14,
              borderRadius: 10,
              alignSelf: 'flex-start',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
            }}
          >
            <Text
              style={{
                fontFamily: sora(600),
                fontSize: 15,
                color: '#0A1A1A',
              }}
            >
              Browse Startups
            </Text>
            <ChevronRight size={16} color="#0A1A1A" />
          </AnimatedPressable>
        </View>

        {/* Mockup side */}
        <View style={{ flex: isMobile ? undefined : 0.55 }}>
          <InvestorDealflowMockup sora={sora} isMobile={isMobile} />
        </View>
      </View>
    </Animated.View>
  );
}

// ─── Investor Dealflow Mockup ───────────────────────────
function InvestorDealflowMockup({
  sora,
  isMobile,
}: {
  sora: SoraFn;
  isMobile: boolean;
}) {
  const deals = [
    {
      name: 'NovaTech AI',
      industry: 'AI / ML',
      score: 82,
      stage: 'Series A',
      scoreColor: '#34C759',
    },
    {
      name: 'GreenVolt',
      industry: 'CleanTech',
      score: 74,
      stage: 'Seed',
      scoreColor: TEAL,
    },
    {
      name: 'FinLeap',
      industry: 'FinTech',
      score: 68,
      stage: 'Pre-Seed',
      scoreColor: '#FF9500',
    },
  ];

  return (
    <View
      style={webStyle(
        {
          backgroundColor: '#FFFFFF',
          borderRadius: 20,
          padding: isMobile ? 20 : 28,
          borderWidth: 1,
          borderColor: '#E8E8ED',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.08,
          shadowRadius: 24,
          elevation: 6,
        },
        { transform: [{ rotate: '1deg' }] },
      )}
    >
      {/* Header */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
        }}
      >
        <Text
          style={{
            fontFamily: sora(600),
            fontSize: 16,
            color: '#1D1D1F',
          }}
        >
          Dealflow Pipeline
        </Text>
      </View>

      {/* Pipeline stats */}
      <View
        style={{
          flexDirection: 'row',
          gap: 12,
          marginBottom: 18,
        }}
      >
        {[
          { label: 'New', count: 12, color: TEAL },
          { label: 'In Review', count: 8, color: '#FF9500' },
          { label: 'Due Diligence', count: 3, color: '#34C759' },
        ].map((s) => (
          <View
            key={s.label}
            style={{
              flex: 1,
              paddingVertical: 10,
              paddingHorizontal: 10,
              borderRadius: 10,
              backgroundColor: LIGHT_BG,
              alignItems: 'center',
            }}
          >
            <Text
              style={{
                fontFamily: sora(700),
                fontSize: 18,
                color: s.color,
              }}
            >
              {s.count}
            </Text>
            <Text
              style={{
                fontFamily: sora(400),
                fontSize: 10,
                color: '#8E8E93',
                marginTop: 2,
              }}
            >
              {s.label}
            </Text>
          </View>
        ))}
      </View>

      {/* Deal cards */}
      {deals.map((d, i) => (
        <View
          key={d.name}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 12,
            borderTopWidth: i > 0 ? 1 : 0,
            borderTopColor: '#F5F5F7',
          }}
        >
          {/* Avatar placeholder */}
          <View
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              backgroundColor: TEAL_DIM,
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 12,
            }}
          >
            <Text
              style={{
                fontFamily: sora(700),
                fontSize: 14,
                color: TEAL,
              }}
            >
              {d.name[0]}
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontFamily: sora(600),
                fontSize: 14,
                color: '#1D1D1F',
              }}
            >
              {d.name}
            </Text>
            <View
              style={{
                flexDirection: 'row',
                gap: 8,
                marginTop: 3,
              }}
            >
              <Text
                style={{
                  fontFamily: sora(400),
                  fontSize: 11,
                  color: '#8E8E93',
                }}
              >
                {d.industry}
              </Text>
              <Text
                style={{
                  fontFamily: sora(400),
                  fontSize: 11,
                  color: '#AEAEB2',
                }}
              >
                {d.stage}
              </Text>
            </View>
          </View>
          {/* Score badge */}
          <View
            style={{
              paddingHorizontal: 10,
              paddingVertical: 4,
              borderRadius: 8,
              backgroundColor: `${d.scoreColor}18`,
            }}
          >
            <Text
              style={{
                fontFamily: sora(700),
                fontSize: 14,
                color: d.scoreColor,
              }}
            >
              {d.score}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

// ─── 5. How It Works ────────────────────────────────────
function HowItWorks({
  isMobile,
  isDesktop,
  sora,
}: {
  isMobile: boolean;
  isDesktop: boolean;
  sora: SoraFn;
}) {
  const steps = [
    {
      num: '1',
      title: 'Create Profile',
      desc: 'Sign up as a startup or investor and complete your profile in under 5 minutes.',
    },
    {
      num: '2',
      title: 'AI Analysis',
      desc: 'Our AI scores your investment readiness across 15+ criteria and generates actionable insights.',
    },
    {
      num: '3',
      title: 'Connect',
      desc: 'Get matched with investors, share your data room, and receive introductions.',
    },
  ];

  return (
    <Animated.View
      entering={FadeInUp.delay(200).duration(600)}
      style={{
        paddingHorizontal: isMobile ? 20 : 64,
        paddingVertical: isMobile ? 48 : 80,
        backgroundColor: '#FFFFFF',
      }}
    >
      <View
        style={{
          maxWidth: 900,
          alignSelf: 'center',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Text
          style={{
            fontFamily: sora(700),
            fontSize: isMobile ? 26 : 36,
            color: '#1D1D1F',
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          How It Works
        </Text>
        <Text
          style={{
            fontFamily: sora(400),
            fontSize: isMobile ? 15 : 18,
            color: '#636366',
            textAlign: 'center',
            marginBottom: 48,
            maxWidth: 500,
          }}
        >
          Get started in three simple steps — no complex setup required.
        </Text>

        <View
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 36 : 0,
            width: '100%',
          }}
        >
          {steps.map((step, i) => (
            <View
              key={step.num}
              style={{
                flex: isMobile ? undefined : 1,
                alignItems: 'center',
                paddingHorizontal: isMobile ? 0 : 20,
              }}
            >
              {/* Connector line (desktop only, not for first) */}
              {!isMobile && i > 0 && (
                <View
                  style={{
                    position: 'absolute',
                    top: 24,
                    left: -20,
                    width: 40,
                    height: 1,
                    borderStyle: 'dashed',
                    borderTopWidth: 2,
                    borderTopColor: '#D2D2D7',
                  }}
                />
              )}
              {/* Number circle */}
              <View
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 24,
                  backgroundColor: '#0D2626',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16,
                }}
              >
                <Text
                  style={{
                    fontFamily: sora(700),
                    fontSize: 20,
                    color: TEAL,
                  }}
                >
                  {step.num}
                </Text>
              </View>
              <Text
                style={{
                  fontFamily: sora(600),
                  fontSize: 17,
                  color: '#1D1D1F',
                  textAlign: 'center',
                  marginBottom: 8,
                }}
              >
                {step.title}
              </Text>
              <Text
                style={{
                  fontFamily: sora(400),
                  fontSize: 14,
                  color: '#636366',
                  textAlign: 'center',
                  lineHeight: 21,
                  maxWidth: 240,
                }}
              >
                {step.desc}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </Animated.View>
  );
}

// ─── 6. Pricing Preview ─────────────────────────────────
function PricingPreview({
  isMobile,
  isDesktop,
  sora,
  router,
}: {
  isMobile: boolean;
  isDesktop: boolean;
  sora: SoraFn;
  router: RouterType;
}) {
  const [tab, setTab] = useState<'startups' | 'investors'>('startups');

  const tiers: PricingTier[] =
    tab === 'startups'
      ? startupPricing.filter((t) => t.id === 'startup-free' || t.id === 'startup-standard')
      : investorPricing.filter((t) => t.id === 'investor-starter' || t.id === 'investor-standard');

  return (
    <Animated.View
      entering={FadeInUp.delay(200).duration(600)}
      style={{
        paddingHorizontal: isMobile ? 20 : 64,
        paddingVertical: isMobile ? 48 : 80,
        backgroundColor: '#FFFFFF',
      }}
    >
      <View
        style={{
          maxWidth: 900,
          alignSelf: 'center',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Text
          style={{
            fontFamily: sora(700),
            fontSize: isMobile ? 26 : 36,
            color: '#1D1D1F',
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          Simple, Transparent Pricing
        </Text>
        <Text
          style={{
            fontFamily: sora(400),
            fontSize: isMobile ? 15 : 18,
            color: '#636366',
            textAlign: 'center',
            marginBottom: 28,
          }}
        >
          Start free. Upgrade when you're ready.
        </Text>

        {/* Tab toggle */}
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: LIGHT_BG,
            borderRadius: 12,
            padding: 4,
            marginBottom: 32,
          }}
        >
          {(['startups', 'investors'] as const).map((t) => (
            <AnimatedPressable
              key={t}
              onPress={() => setTab(t)}
              style={{
                paddingHorizontal: 24,
                paddingVertical: 10,
                borderRadius: 10,
                backgroundColor: tab === t ? '#FFFFFF' : 'transparent',
                ...(tab === t
                  ? {
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.08,
                      shadowRadius: 4,
                      elevation: 2,
                    }
                  : {}),
              }}
            >
              <Text
                style={{
                  fontFamily: sora(tab === t ? 600 : 500),
                  fontSize: 14,
                  color: tab === t ? '#1D1D1F' : '#8E8E93',
                  textTransform: 'capitalize',
                }}
              >
                {t}
              </Text>
            </AnimatedPressable>
          ))}
        </View>

        {/* Pricing cards */}
        <View
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 16 : 24,
            width: '100%',
          }}
        >
          {tiers.map((tier) => (
            <View
              key={tier.id}
              style={{
                flex: isMobile ? undefined : 1,
                backgroundColor: '#FFFFFF',
                borderRadius: 16,
                padding: isMobile ? 20 : 28,
                borderWidth: tier.highlighted ? 2 : 1,
                borderColor: tier.highlighted ? TEAL : '#E8E8ED',
                position: 'relative',
              }}
            >
              {tier.popular && (
                <View
                  style={{
                    position: 'absolute',
                    top: -12,
                    alignSelf: 'center',
                    backgroundColor: TEAL,
                    paddingHorizontal: 14,
                    paddingVertical: 4,
                    borderRadius: 12,
                  }}
                >
                  <Text
                    style={{
                      fontFamily: sora(600),
                      fontSize: 11,
                      color: '#0A1A1A',
                    }}
                  >
                    Most Popular
                  </Text>
                </View>
              )}
              <Text
                style={{
                  fontFamily: sora(600),
                  fontSize: 18,
                  color: '#1D1D1F',
                  marginBottom: 4,
                  marginTop: tier.popular ? 8 : 0,
                }}
              >
                {tier.name}
              </Text>
              <Text
                style={{
                  fontFamily: sora(400),
                  fontSize: 13,
                  color: '#8E8E93',
                  marginBottom: 16,
                }}
              >
                {tier.tagline}
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'baseline', marginBottom: 20 }}>
                <Text
                  style={{
                    fontFamily: sora(700),
                    fontSize: 36,
                    color: '#1D1D1F',
                  }}
                >
                  {tier.price === 0 ? 'Free' : `${CURRENCY} ${tier.price}`}
                </Text>
                {tier.price > 0 && (
                  <Text
                    style={{
                      fontFamily: sora(400),
                      fontSize: 14,
                      color: '#8E8E93',
                      marginLeft: 4,
                    }}
                  >
                    /mo
                  </Text>
                )}
              </View>
              {tier.features.slice(0, 5).map((f, i) => (
                <View
                  key={i}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <Check size={14} color={TEAL} strokeWidth={3} />
                  <Text
                    style={{
                      fontFamily: sora(400),
                      fontSize: 13,
                      color: '#636366',
                      flex: 1,
                    }}
                  >
                    {f}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* View all plans */}
        <AnimatedPressable
          onPress={() => router.push('/pricing')}
          style={{
            marginTop: 28,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Text
            style={{
              fontFamily: sora(600),
              fontSize: 15,
              color: '#009999',
            }}
          >
            View All Plans
          </Text>
          <ArrowRight size={16} color="#009999" />
        </AnimatedPressable>
      </View>
    </Animated.View>
  );
}

// ─── 7. Security & Trust ────────────────────────────────
function SecuritySection({
  isMobile,
  isDesktop,
  sora,
}: {
  isMobile: boolean;
  isDesktop: boolean;
  sora: SoraFn;
}) {
  const items = [
    {
      icon: Shield,
      title: 'EU Data Hosting',
      desc: 'Database and files hosted in Frankfurt, Germany (EU), processed under the Swiss FADP and, where applicable, the GDPR.',
    },
    {
      icon: Lock,
      title: 'Encrypted in Transit & at Rest',
      desc: 'Data is encrypted in transit (TLS 1.2+) and at rest.',
    },
    {
      icon: Database,
      title: 'Row-Level Security',
      desc: 'Database policies ensure users only access their own data.',
    },
    {
      icon: Key,
      title: 'Your Data, Your Rules',
      desc: 'Full control over data sharing. Revoke access at any time.',
    },
  ];

  return (
    <Animated.View
      entering={FadeInUp.delay(200).duration(600)}
      style={{
        backgroundColor: SECURITY_DARK,
        paddingHorizontal: isMobile ? 20 : 64,
        paddingVertical: isMobile ? 48 : 80,
      }}
    >
      <View
        style={{
          maxWidth: 1000,
          alignSelf: 'center',
          width: '100%',
        }}
      >
        <Text
          style={{
            fontFamily: sora(700),
            fontSize: isMobile ? 26 : 36,
            color: '#FFFFFF',
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          Enterprise-Grade Security
        </Text>
        <Text
          style={{
            fontFamily: sora(400),
            fontSize: isMobile ? 15 : 18,
            color: 'rgba(255,255,255,0.6)',
            textAlign: 'center',
            marginBottom: 40,
            maxWidth: 500,
            alignSelf: 'center',
          }}
        >
          Built in Switzerland. Your data is protected by design.
        </Text>

        <View
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: isMobile ? 16 : 20,
          }}
        >
          {items.map((item) => (
            <View
              key={item.title}
              style={{
                flex: isMobile ? undefined : 1,
                minWidth: isMobile ? undefined : 200,
                backgroundColor: 'rgba(255,255,255,0.05)',
                borderRadius: 16,
                padding: isMobile ? 20 : 24,
                borderWidth: 1,
                borderColor: 'rgba(255,255,255,0.08)',
              }}
            >
              <View
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  backgroundColor: 'rgba(0,217,217,0.12)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                <item.icon size={22} color={TEAL} />
              </View>
              <Text
                style={{
                  fontFamily: sora(600),
                  fontSize: 16,
                  color: '#FFFFFF',
                  marginBottom: 6,
                }}
              >
                {item.title}
              </Text>
              <Text
                style={{
                  fontFamily: sora(400),
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.55)',
                  lineHeight: 19,
                }}
              >
                {item.desc}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </Animated.View>
  );
}

// ─── 8. Final CTA ───────────────────────────────────────
function FinalCTA({
  isMobile,
  sora,
  router,
}: {
  isMobile: boolean;
  sora: SoraFn;
  router: RouterType;
}) {
  // Breathing pulse
  const pulseScale = useSharedValue(1);
  useEffect(() => {
    pulseScale.value = withRepeat(
      withTiming(1.06, {
        duration: 2000,
        easing: Easing.inOut(Easing.ease),
      }),
      -1,
      true,
    );
  }, []);
  const pulseStyle = useAnimatedStyle(() => ({
    transform: [{ scale: pulseScale.value }],
  }));

  return (
    <LinearGradient
      colors={['#009999', '#00B8B8']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={{
        paddingVertical: isMobile ? 56 : 80,
        paddingHorizontal: isMobile ? 20 : 64,
      }}
    >
      <View
        style={{
          maxWidth: 700,
          alignSelf: 'center',
          width: '100%',
          alignItems: 'center',
        }}
      >
        {/* Pulsing circle accent */}
        <Animated.View
          style={[
            pulseStyle,
            {
              width: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: 'rgba(255,255,255,0.2)',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 24,
            },
          ]}
        >
          <View
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: '#FFFFFF',
            }}
          />
        </Animated.View>

        <Text
          style={{
            fontFamily: sora(700),
            fontSize: isMobile ? 26 : 38,
            color: '#FFFFFF',
            textAlign: 'center',
            marginBottom: 14,
          }}
        >
          Ready to Transform{'\n'}Your Fundraising?
        </Text>
        <Text
          style={{
            fontFamily: sora(400),
            fontSize: isMobile ? 15 : 18,
            color: 'rgba(255,255,255,0.85)',
            textAlign: 'center',
            marginBottom: 32,
            maxWidth: 500,
          }}
        >
          Score your startup or screen your dealflow with Inverio.
        </Text>

        <View
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: 14,
            width: isMobile ? '100%' : undefined,
          }}
        >
          <AnimatedPressable
            onPress={() => router.push('/login?role=startup')}
            style={{
              backgroundColor: '#FFFFFF',
              paddingHorizontal: 32,
              paddingVertical: 16,
              borderRadius: 12,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <Text
              style={{
                fontFamily: sora(600),
                fontSize: 16,
                color: '#009999',
              }}
            >
              Get Your Score
            </Text>
            <ArrowRight size={18} color="#009999" />
          </AnimatedPressable>

          <AnimatedPressable
            onPress={() => router.push('/login?role=investor')}
            style={{
              paddingHorizontal: 32,
              paddingVertical: 16,
              borderRadius: 12,
              borderWidth: 1.5,
              borderColor: 'rgba(255,255,255,0.4)',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <Text
              style={{
                fontFamily: sora(600),
                fontSize: 16,
                color: '#FFFFFF',
              }}
            >
              Explore Dealflow
            </Text>
            <ArrowRight size={18} color="#FFFFFF" />
          </AnimatedPressable>
        </View>
      </View>
    </LinearGradient>
  );
}
